import { Immutable } from "immer";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import iconAmericanExpress from "../../../../img/checkout/american-express.png";
import iconCCBack from "../../../../img/checkout/cc-back.png";
import creditCards from "../../../../img/checkout/credit-cards.png";
import iconDiscover from "../../../../img/checkout/discover.png";
import iconLock from "../../../../img/checkout/lock.png";
import iconMastercard from "../../../../img/checkout/mastercard.png";
import iconVisa from "../../../../img/checkout/visa.png";
import {
    createCaptureContext,
    decodeCaptureContextJwt,
    decodeTransientTokenCard,
    decodeTransientTokenValidity,
    loadFlexSdk,
} from "../../../api/microform";
import type {
    MicroformChangeData,
    MicroformField,
    MicroformInstance,
} from "../../../api/microform-types";
import { Image } from "../../../components/Image";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { FormNumber } from "../../../forms/FormNumber";
import {
    FORM_ERROR_CLASS_NAME,
    FORM_ERROR_LIST_CLASS_NAME,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_FOCUSED_CLASS_NAME,
    FORM_FIELD_HAS_ERRORS_CLASS_NAME,
    FORM_FIELD_REQUIRED_CLASS_NAME,
} from "../../../forms/classNames";
import { useAppDispatch } from "../../../store/reducers";
import { format } from "../../../utils/format";
import { IPaymentMethodFormProps } from "../PaymentMethod.interfaces";
import { Step } from "../constants";
import { setPaymentMethodFields } from "../reducers";
import { IMicroformPayment } from "../reducers.interfaces";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CARD_TYPE_ICONS: Readonly<Record<string, string>> = {
    "001": iconVisa,
    "002": iconMastercard,
    "003": iconAmericanExpress,
    "004": iconDiscover,
};

/**
 * Build Microform iframe styles by reading computed values from the page so the
 * iframe fields automatically match whatever site/skin is active.
 */
const buildMicroformStyles = () => {
    const win = getWindow();
    const doc = win?.document;
    let fontFamily = "sans-serif";
    let color = "#000";
    let placeholderColor = "rgb(117, 117, 117)";

    if (win && doc) {
        // Read font & color from the body so we inherit each site's typography.
        const bodyStyle = win.getComputedStyle(doc.body);
        fontFamily = bodyStyle.fontFamily || fontFamily;
        color = bodyStyle.color || color;

        // Read placeholder color from the CSS variable or use the shared default
        // set in _form.scss (rgb(117 117 117)).
        const rootStyle = win.getComputedStyle(doc.documentElement);
        const customPlaceholder = rootStyle
            .getPropertyValue("--form--placeholder-color")
            .trim();
        if (customPlaceholder) {
            placeholderColor = customPlaceholder;
        }
    }

    return {
        "input": {
            "font-size": "16px",
            "font-family": fontFamily,
            "color": color,
            "line-height": "1.5",
        },
        "::placeholder": {
            color: placeholderColor,
        },
    };
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SdkState = Immutable<
    | { status: "loading" }
    | { status: "ready"; microform: MicroformInstance }
    | { status: "error"; message: string }
>;

type IFieldState = Immutable<{
    loaded: boolean;
    focused: boolean;
    valid: boolean;
    empty: boolean;
    couldBeValid: boolean;
    showError: boolean;
}>;

const INITIAL_FIELD_STATE: IFieldState = {
    loaded: false,
    focused: false,
    valid: false,
    empty: true,
    couldBeValid: true,
    showError: false,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type IProps = Immutable<
    IPaymentMethodFormProps & {
        changeAmount: (methodKey: string, amount: string) => void;
    }
>;

const getMethodData = (
    form: IProps["form"],
    methodKey: string,
): IMicroformPayment => {
    const data = form.payment_methods[methodKey];
    let mfData: IMicroformPayment | null = null;
    if (data.method_type === "cybersource-microform") {
        mfData = data;
    }
    const baseFields: IMicroformPayment = {
        method_type: "cybersource-microform",
        amount: "",
        pay_balance: false,
        transient_token: "",
        card_type: "",
        card_number: "",
    };
    return {
        ...baseFields,
        ...mfData,
    };
};

/**
 * Generate month options for expiry select (01–12).
 */
const buildMonthOptions = (): ReadonlyArray<{ value: string; label: string }> =>
    Array.from({ length: 12 }, (_, i) => {
        const m = String(i + 1).padStart(2, "0");
        return { value: m, label: m };
    });

/**
 * Generate year options for expiry select (current year through current year + 10).
 */
const buildYearOptions = (): ReadonlyArray<{
    value: string;
    label: string;
}> => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 11 }, (_, i) => {
        const y = String(currentYear + i);
        return { value: y, label: y };
    });
};

/**
 * Whether the selected expiry month has already ended. CyberSource rejects a
 * token for such a card, so it is caught before tokenizing.
 */
const isExpiryInPast = (month: string, year: string): boolean => {
    if (month === "" || year === "") {
        return false;
    }
    const now = new Date();
    const y = parseInt(year, 10);
    return (
        y < now.getFullYear() ||
        (y === now.getFullYear() && parseInt(month, 10) < now.getMonth() + 1)
    );
};

const MONTH_OPTIONS = buildMonthOptions();

/**
 * How long before a transient token expires to reload the fields, so a token
 * is never submitted in the last moments of its life.
 */
const TOKEN_EXPIRY_MARGIN_MS = 60 * 1000;

/**
 * When each transient token reached this browser, by its clock. A token's
 * lifetime is timed from here, not from its `exp` claim, which is on
 * CyberSource's clock: a customer whose clock is off would otherwise have
 * every token reset the moment it arrives.
 */
const tokenReceivedAt = new Map<string, number>();

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Microform = (props: IProps): React.ReactNode => {
    const dispatch = useAppDispatch();

    // SDK lifecycle state
    const [sdkState, setSdkState] = useState<SdkState>({ status: "loading" });

    // Field validation state
    const [numberState, setNumberState] =
        useState<IFieldState>(INITIAL_FIELD_STATE);
    const [cvvState, setCvvState] = useState<IFieldState>(INITIAL_FIELD_STATE);

    // Detected card type from Microform change event (e.g. "001" for Visa)
    const [detectedCardType, setDetectedCardType] = useState<string | null>(
        null,
    );

    // Expiry selects
    const [expMonth, setExpMonth] = useState<string>("");
    const [expYear, setExpYear] = useState<string>("");
    const [expShowError, setExpShowError] = useState<boolean>(false);

    // Tokenization state
    const [tokenizing, setTokenizing] = useState<boolean>(false);

    // Refs for cleanup
    const numberFieldRef = useRef<MicroformField | null>(null);
    const cvvFieldRef = useRef<MicroformField | null>(null);
    const hasStartedRef = useRef(false);
    const clearTokenRef = useRef<() => void>(() => {});
    const initMicroformRef = useRef<() => Promise<void>>(async () => {});
    const handleRetryRef = useRef<() => void>(() => {});
    const tokenizingRef = useRef(false);

    const YEAR_OPTIONS = React.useMemo(buildYearOptions, []);

    const methodData = getMethodData(props.form, props.methodKey);
    const transientToken = methodData.transient_token;

    const sectionName = props.header
        ? `_${props.header.replace(/ +/g, "_").toLowerCase()}`
        : "";

    // -----------------------------------------------------------------------
    // Clear transient token when field data changes
    // -----------------------------------------------------------------------

    const clearToken = useCallback(() => {
        if (transientToken) {
            tokenReceivedAt.delete(transientToken);
            dispatch(
                setPaymentMethodFields({
                    method_key: props.methodKey,
                    fields: {
                        transient_token: "",
                        card_type: "",
                        card_number: "",
                    },
                }),
            );
        }
    }, [dispatch, props.methodKey, transientToken]);
    clearTokenRef.current = clearToken;

    // -----------------------------------------------------------------------
    // Tokenize
    // -----------------------------------------------------------------------

    const tokenize = useCallback(
        (microform: MicroformInstance) => {
            if (tokenizingRef.current) {
                return;
            }
            tokenizingRef.current = true;
            setTokenizing(true);
            microform.createToken(
                { expirationMonth: expMonth, expirationYear: expYear },
                (err, token) => {
                    tokenizingRef.current = false;
                    setTokenizing(false);
                    if (err) {
                        console.error("Microform createToken error:", err);
                        // Re-initialize the microform to get a fresh capture
                        // context (the most likely cause is an expired JWT).
                        void initMicroformRef.current();
                        return;
                    }
                    // The token carries the masked number the split pay
                    // summary shows. Prefer the type the number field detected
                    // so the summary brand matches the in-field card logo; the
                    // token's detectedCardTypes is only a fallback.
                    tokenReceivedAt.set(token, Date.now());
                    const card = decodeTransientTokenCard(token);
                    dispatch(
                        setPaymentMethodFields({
                            method_key: props.methodKey,
                            fields: {
                                transient_token: token,
                                card_type: detectedCardType || card.cardType,
                                card_number: card.maskedNumber,
                            },
                        }),
                    );
                },
            );
        },
        [dispatch, detectedCardType, expMonth, expYear, props.methodKey],
    );

    // -----------------------------------------------------------------------
    // Auto-tokenize when all fields valid
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (
            sdkState.status === "ready" &&
            numberState.valid &&
            cvvState.valid &&
            expMonth !== "" &&
            expYear !== "" &&
            !isExpiryInPast(expMonth, expYear) &&
            !tokenizing &&
            !transientToken
        ) {
            tokenize(sdkState.microform);
        }
    }, [
        sdkState,
        numberState.valid,
        cvvState.valid,
        expMonth,
        expYear,
        tokenizing,
        transientToken,
        tokenize,
    ]);

    // -----------------------------------------------------------------------
    // Initialize Microform SDK
    // -----------------------------------------------------------------------

    const initMicroform = useCallback(async () => {
        const win = getWindow();
        try {
            setSdkState({ status: "loading" });
            // The new fields load empty; stale validity would auto-tokenize
            // against them before they load.
            setNumberState(INITIAL_FIELD_STATE);
            setCvvState(INITIAL_FIELD_STATE);
            setDetectedCardType(null);

            // Step 1: Get capture context from backend
            const { capture_context } = await createCaptureContext();

            // Step 2: Decode JWT to get SDK URL and SRI hash
            const { clientLibrary, clientLibraryIntegrity } =
                decodeCaptureContextJwt(capture_context);

            // Step 3: Load Flex SDK (singleton)
            await loadFlexSdk(clientLibrary, clientLibraryIntegrity);

            if (!win?.Flex) {
                throw new Error("Flex SDK failed to initialize");
            }

            // Step 4: Initialize Flex → Microform → Fields
            const flex = new win.Flex(capture_context);
            const microform = flex.microform({
                styles: buildMicroformStyles(),
            });

            // Create number field
            const numberField = microform.createField("number", {
                "placeholder": t`Card Number`,
                "aria-label": t`Card Number`,
                "aria-required": true,
            });

            // Create security code field
            const cvvField = microform.createField("securityCode", {
                "placeholder": t`Security Code`,
                "aria-label": t`Security Code`,
                "aria-required": true,
                "maxLength": 4,
            });

            numberFieldRef.current = numberField;
            cvvFieldRef.current = cvvField;

            // Subscribe to number field events
            numberField.on("change", (data: MicroformChangeData) => {
                if (data.card && data.card.length > 0) {
                    setDetectedCardType(data.card[0].cybsCardType);
                } else {
                    setDetectedCardType(null);
                }
                setNumberState((prev) => ({
                    ...prev,
                    valid: data.valid,
                    empty: data.empty,
                    couldBeValid: data.couldBeValid,
                }));
                clearTokenRef.current();
            });

            numberField.on("focus", () => {
                setNumberState((prev) => ({ ...prev, focused: true }));
            });

            numberField.on("blur", () => {
                setNumberState((prev) => ({
                    ...prev,
                    focused: false,
                    showError: true,
                }));
            });

            numberField.on("load", () => {
                setNumberState((prev) => ({ ...prev, loaded: true }));
            });

            // Subscribe to CVV field events
            cvvField.on("change", (data: MicroformChangeData) => {
                setCvvState((prev) => ({
                    ...prev,
                    valid: data.valid,
                    empty: data.empty,
                    couldBeValid: data.couldBeValid,
                }));
                clearTokenRef.current();
            });

            cvvField.on("focus", () => {
                setCvvState((prev) => ({ ...prev, focused: true }));
            });

            cvvField.on("blur", () => {
                setCvvState((prev) => ({
                    ...prev,
                    focused: false,
                    showError: true,
                }));
            });

            cvvField.on("load", () => {
                setCvvState((prev) => ({ ...prev, loaded: true }));
            });

            // Step 5: Set state to ready so containers render, then load fields
            setSdkState({ status: "ready", microform });
        } catch (err) {
            console.error("Microform init error:", err);
            setSdkState({
                status: "error",
                message: t`An error occurred while loading the payment form.`,
            });
        }
    }, []); // No deps needed - all mutable state accessed via refs or setState
    initMicroformRef.current = initMicroform;

    // Load fields into containers once SDK is ready and containers are rendered
    useEffect(() => {
        if (sdkState.status !== "ready") {
            return;
        }
        const numberContainer = `#number-container-${props.methodKey}`;
        const cvvContainer = `#securityCode-container-${props.methodKey}`;

        if (numberFieldRef.current) {
            numberFieldRef.current.load(numberContainer);
        }
        if (cvvFieldRef.current) {
            cvvFieldRef.current.load(cvvContainer);
        }
    }, [sdkState.status, props.methodKey]);

    // Initialize on mount
    useEffect(() => {
        if (!hasStartedRef.current && !props.disabled) {
            hasStartedRef.current = true;
            void initMicroform();
        }
    }, [initMicroform, props.disabled]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (numberFieldRef.current) {
                try {
                    numberFieldRef.current.dispose();
                } catch {
                    // ignore disposal errors
                }
            }
            if (cvvFieldRef.current) {
                try {
                    cvvFieldRef.current.dispose();
                } catch {
                    // ignore disposal errors
                }
            }
        };
    }, []);

    // -----------------------------------------------------------------------
    // Event Handlers
    // -----------------------------------------------------------------------

    const handleRetry = () => {
        // Dispose existing fields to prevent leaks
        if (numberFieldRef.current) {
            try {
                numberFieldRef.current.dispose();
            } catch {
                // ignore disposal errors
            }
            numberFieldRef.current = null;
        }
        if (cvvFieldRef.current) {
            try {
                cvvFieldRef.current.dispose();
            } catch {
                // ignore disposal errors
            }
            cvvFieldRef.current = null;
        }

        hasStartedRef.current = false;
        setExpMonth("");
        setExpYear("");
        setExpShowError(false);
        tokenizingRef.current = false;
        setTokenizing(false);
        tokenReceivedAt.delete(transientToken);
        dispatch(
            setPaymentMethodFields({
                method_key: props.methodKey,
                fields: {
                    transient_token: "",
                    card_type: "",
                    card_number: "",
                },
            }),
        );
        void initMicroform();
    };

    handleRetryRef.current = handleRetry;

    // -----------------------------------------------------------------------
    // Reload the fields before the transient token expires
    // -----------------------------------------------------------------------

    // CyberSource rejects an expired token with a 400, and the token outlives
    // the page: a customer who idles, or retries after a decline, would
    // otherwise submit a stale one. Its capture context has expired by then
    // too, so the card has to be entered again into fresh fields.
    useEffect(() => {
        // A pre-authorized card, or one in an order being placed, keeps its
        // token; the fields must be live before they can be rebuilt.
        if (
            !transientToken ||
            props.disabled ||
            props.form.current_step === Step.PLACING_ORDER ||
            sdkState.status !== "ready"
        ) {
            return;
        }
        const validity = decodeTransientTokenValidity(transientToken);
        if (validity === null) {
            return;
        }
        const lifetime = Math.max(validity.expiresAt - validity.issuedAt, 0);
        const receivedAt =
            tokenReceivedAt.get(transientToken) ?? validity.issuedAt;
        const remaining =
            lifetime - TOKEN_EXPIRY_MARGIN_MS - (Date.now() - receivedAt);
        const timer = setTimeout(
            () => handleRetryRef.current(),
            Math.min(Math.max(remaining, 0), lifetime),
        );
        return () => clearTimeout(timer);
    }, [
        transientToken,
        props.disabled,
        props.form.current_step,
        sdkState.status,
    ]);

    const onExpMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setExpMonth(event.target.value);
        clearToken();
    };

    const onExpYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setExpYear(event.target.value);
        clearToken();
    };

    const onExpBlur = (event: React.FocusEvent<HTMLDivElement>) => {
        // Month and year are halves of one field: moving between them is not
        // leaving it (#36338).
        if (event.currentTarget.contains(event.relatedTarget)) {
            return;
        }
        setExpShowError(true);
    };

    const onAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeAmount(props.methodKey, event.currentTarget.value);
    };

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        props.onValidStateChange(fieldName, errorMessages, props.methodKey);
    };

    // -----------------------------------------------------------------------
    // Computed Values
    // -----------------------------------------------------------------------

    const errors =
        props.methodKey in props.errors ? props.errors[props.methodKey] : {};

    const cardIcon =
        detectedCardType && detectedCardType in CARD_TYPE_ICONS
            ? CARD_TYPE_ICONS[detectedCardType]
            : iconLock;

    // Each predicate drives both the field's error class and its error list.
    const showNumberError =
        numberState.showError && !numberState.valid && !numberState.empty;
    const showCvvError =
        cvvState.showError && !cvvState.valid && !cvvState.empty;
    const showExpError = expShowError && (expMonth === "" || expYear === "");
    const showExpPastError = isExpiryInPast(expMonth, expYear);

    const numberFieldClasses = concatClassNames([
        "microform-field",
        numberState.focused ? "microform-field--focused" : null,
        showNumberError ? "microform-field--invalid" : null,
    ]);

    const cvvFieldClasses = concatClassNames([
        "microform-field",
        cvvState.focused ? "microform-field--focused" : null,
        showCvvError ? "microform-field--invalid" : null,
    ]);

    const numberWrapperClasses = concatClassNames([
        FORM_FIELD_CLASS_NAME,
        FORM_FIELD_REQUIRED_CLASS_NAME,
        "form__field--card_number",
        showNumberError ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
        numberState.focused ? FORM_FIELD_FOCUSED_CLASS_NAME : null,
    ]);

    const cvvWrapperClasses = concatClassNames([
        FORM_FIELD_CLASS_NAME,
        FORM_FIELD_REQUIRED_CLASS_NAME,
        "form__field--card_cvc",
        showCvvError ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
        cvvState.focused ? FORM_FIELD_FOCUSED_CLASS_NAME : null,
    ]);

    const expWrapperClasses = concatClassNames([
        FORM_FIELD_CLASS_NAME,
        FORM_FIELD_REQUIRED_CLASS_NAME,
        "form__field--card_expiration",
        showExpError || showExpPastError
            ? FORM_FIELD_HAS_ERRORS_CLASS_NAME
            : null,
    ]);

    const creditCardImage = creditCards;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <div>
            <h4 className="checkout-step__bar-heading">{props.header}</h4>
            <div className="checkout-step__form-container checkout-step__form-container--cc checkout-step__form-container--blue">
                <div className="checkout-step__form-header">
                    <div className="checkout-step__form-header--cc-copy">
                        <h5 className="checkout-step__copy--bold">
                            {t`Secure Credit Card Payment`}
                        </h5>
                        <div>
                            {t`Use your VISA, MasterCard, American Express or Discover credit card.`}
                        </div>
                    </div>
                    <div className="checkout-step__form-header--cc-image">
                        <Image
                            src={creditCardImage}
                            className="checkout-step__form-image--cc responsive-img"
                            alt={t`Accepted Credit Cards: VISA, MasterCard, American Express or Discover`}
                        />
                    </div>
                </div>
                <fieldset disabled={props.disabled} style={{ border: 0 }}>
                    <legend className="ada-screenreader-only">
                        {interpolate(t`%s Information`, [
                            !props.header ? t`Credit Card` : props.header,
                        ])}
                    </legend>

                    {/* SDK Error State */}
                    {sdkState.status === "error" && (
                        <div className="microform-error">
                            <p>
                                {sdkState.message ||
                                    t`Payment form failed to load.`}
                            </p>
                            <button
                                className="button"
                                onClick={handleRetry}
                                type="button"
                            >
                                {t`Try Again`}
                            </button>
                        </div>
                    )}

                    {/* Loading State */}
                    {sdkState.status === "loading" && (
                        <div className="microform-loading">
                            <LoadingSpinner />
                        </div>
                    )}

                    {/* Card Number Field */}
                    {sdkState.status === "ready" && (
                        <>
                            <div className={numberWrapperClasses}>
                                <label
                                    htmlFor={`number-container-${props.methodKey}`}
                                    className="checkout-step__copy--bold"
                                >
                                    {t`Card Number`}
                                </label>
                                <div className="microform-field__wrapper">
                                    <div
                                        id={`number-container-${props.methodKey}`}
                                        className={numberFieldClasses}
                                    />
                                    <img
                                        src={cardIcon}
                                        alt={t`Secure`}
                                        className="form__field-icon"
                                    />
                                </div>
                                {showNumberError && (
                                    <ul className={FORM_ERROR_LIST_CLASS_NAME}>
                                        <li className={FORM_ERROR_CLASS_NAME}>
                                            {t`Please enter a valid card number.`}
                                        </li>
                                    </ul>
                                )}
                                {errors.card_number &&
                                    errors.card_number.map((msg) => (
                                        <ul
                                            key={msg}
                                            className={
                                                FORM_ERROR_LIST_CLASS_NAME
                                            }
                                        >
                                            <li
                                                className={
                                                    FORM_ERROR_CLASS_NAME
                                                }
                                            >
                                                {msg}
                                            </li>
                                        </ul>
                                    ))}
                            </div>

                            {/* Expiry Selects */}
                            <div className="checkout-step__payment-cc-expiration">
                                <div className={expWrapperClasses}>
                                    <label
                                        htmlFor={`expMonth${sectionName}`}
                                        className="checkout-step__copy--bold"
                                    >
                                        {t`Expiration`}
                                    </label>
                                    <div
                                        className="microform-expiry"
                                        onBlur={onExpBlur}
                                    >
                                        <select
                                            id={`expMonth${sectionName}`}
                                            aria-label={t`Expiration Month`}
                                            value={expMonth}
                                            onChange={onExpMonthChange}
                                        >
                                            <option value="">{t`MM`}</option>
                                            {MONTH_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <select
                                            id={`expYear${sectionName}`}
                                            aria-label={t`Expiration Year`}
                                            value={expYear}
                                            onChange={onExpYearChange}
                                        >
                                            <option value="">{t`YYYY`}</option>
                                            {YEAR_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {showExpError && (
                                        <ul
                                            className={
                                                FORM_ERROR_LIST_CLASS_NAME
                                            }
                                        >
                                            <li
                                                className={
                                                    FORM_ERROR_CLASS_NAME
                                                }
                                            >
                                                {t`Please select an expiration date.`}
                                            </li>
                                        </ul>
                                    )}
                                    {showExpPastError && (
                                        <ul
                                            className={
                                                FORM_ERROR_LIST_CLASS_NAME
                                            }
                                        >
                                            <li
                                                className={
                                                    FORM_ERROR_CLASS_NAME
                                                }
                                            >
                                                {t`This date indicates an expired card. Please enter a valid expiration date.`}
                                            </li>
                                        </ul>
                                    )}
                                    {errors.card_expiration &&
                                        errors.card_expiration.map((msg) => (
                                            <ul
                                                key={msg}
                                                className={
                                                    FORM_ERROR_LIST_CLASS_NAME
                                                }
                                            >
                                                <li
                                                    className={
                                                        FORM_ERROR_CLASS_NAME
                                                    }
                                                >
                                                    {msg}
                                                </li>
                                            </ul>
                                        ))}
                                </div>
                            </div>

                            {/* CVV Field */}
                            <div className="checkout-step__payment-cc-code">
                                <div className={cvvWrapperClasses}>
                                    <label
                                        htmlFor={`securityCode-container-${props.methodKey}`}
                                        className="checkout-step__copy--bold"
                                    >
                                        {t`Security Code`}
                                    </label>
                                    <div className="microform-field__wrapper">
                                        <div
                                            id={`securityCode-container-${props.methodKey}`}
                                            className={cvvFieldClasses}
                                        />
                                        <img
                                            src={iconCCBack}
                                            alt={t`Code on back of card, Secure`}
                                            className="form__field-icon"
                                        />
                                    </div>
                                    {showCvvError && (
                                        <ul
                                            className={
                                                FORM_ERROR_LIST_CLASS_NAME
                                            }
                                        >
                                            <li
                                                className={
                                                    FORM_ERROR_CLASS_NAME
                                                }
                                            >
                                                {t`Please enter a valid security code.`}
                                            </li>
                                        </ul>
                                    )}
                                    {errors.card_cvc &&
                                        errors.card_cvc.map((msg) => (
                                            <ul
                                                key={msg}
                                                className={
                                                    FORM_ERROR_LIST_CLASS_NAME
                                                }
                                            >
                                                <li
                                                    className={
                                                        FORM_ERROR_CLASS_NAME
                                                    }
                                                >
                                                    {msg}
                                                </li>
                                            </ul>
                                        ))}
                                </div>
                            </div>

                            {/* Split pay amount */}
                            {props.isSplitPay && (
                                <div className="checkout-step__payment-amount">
                                    <label
                                        htmlFor={`amount${sectionName}`}
                                        className="checkout-step__copy--bold"
                                    >
                                        {t`Amount`}
                                    </label>
                                    <FormNumber
                                        name="amount"
                                        id={`amount${sectionName}`}
                                        autoComplete=""
                                        step={0.01}
                                        validation={[
                                            "required",
                                            "amount",
                                            "nonzero",
                                        ]}
                                        showErrorMessages={
                                            props.showErrorMessages
                                        }
                                        onValidStateChange={onValidStateChange}
                                        value={methodData.amount || undefined}
                                        errors={errors.amount}
                                        onChange={onAmountChange}
                                        disabled={props.shouldFreezeAmount}
                                    />
                                </div>
                            )}

                            {/* Pre-authorized state */}
                            {props.disabled && (
                                <div>
                                    <h5>{t`Pre-Authorized`}</h5>
                                    <p>
                                        {interpolate(
                                            t`This card has been pre-authorized for the amount of %s.`,
                                            [
                                                format.money(
                                                    methodData.amount || "0.00",
                                                ),
                                            ],
                                        )}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </fieldset>
            </div>
        </div>
    );
};
